import { useEffect, useState } from 'react';
import {
  CompoundEntityRef,
  Entity,
  RELATION_PART_OF,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { CatalogApi } from '@backstage/catalog-client';
import { useApi } from '@backstage/core-plugin-api';
import {
  EntityRefLinks,
  catalogApiRef,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';

// Deduped by ServiceGroup ref, shared across every cell, every card, and
// every page load in this browser session - a row whose ServiceGroup fifty
// other rows also belong to triggers exactly one fetch, not fifty. Never
// evicted: ServiceGroup entities are relatively few and stable, so the
// memory tradeoff is cheap against constant refetching as the user
// paginates/filters back and forth, or moves between cards.
const serviceGroupCache = new Map<string, Promise<Entity | undefined>>();

function fetchServiceGroup(catalogApi: CatalogApi, ref: CompoundEntityRef) {
  const key = stringifyEntityRef(ref);
  let promise = serviceGroupCache.get(key);
  if (!promise) {
    promise = catalogApi.getEntityByRef(ref);
    serviceGroupCache.set(key, promise);
  }
  return promise;
}

/**
 * Renders System for any entity, falling back through its ServiceGroup
 * relation when there's no direct <kind>->System relation. Shared by every
 * table that shows a System column - the Catalog index page, Provided APIs,
 * and Depends on Components all use this same cell, so they resolve and
 * cache identically rather than each growing their own copy of this logic.
 */
export function SystemCell({ entity }: { entity: Entity }) {
  const catalogApi = useApi(catalogApiRef);
  const directSystemRefs = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'system',
  });
  const serviceGroupRefs = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'servicegroup',
  });
  const serviceGroupKey = serviceGroupRefs
    .map(ref => stringifyEntityRef(ref))
    .join(',');

  const [resolvedSystemRefs, setResolvedSystemRefs] =
    useState(directSystemRefs);

  useEffect(() => {
    // A direct <kind>->System relation (spec.system set) always wins -
    // matches the stock column's behaviour, and covers any future entity
    // that sets both.
    if (directSystemRefs.length > 0 || serviceGroupRefs.length === 0) {
      return undefined;
    }
    let cancelled = false;
    Promise.all(
      serviceGroupRefs.map(ref => fetchServiceGroup(catalogApi, ref)),
    ).then(serviceGroups => {
      if (cancelled) return;
      const systems = serviceGroups
        .filter((e): e is Entity => Boolean(e))
        .flatMap(sg =>
          getEntityRelations(sg, RELATION_PART_OF, { kind: 'system' }),
        );
      setResolvedSystemRefs(systems);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogApi, serviceGroupKey]);

  if (resolvedSystemRefs.length === 0) {
    return null;
  }
  return (
    <EntityRefLinks entityRefs={resolvedSystemRefs} defaultKind="system" />
  );
}
